import {announce} from '@github-ui/aria-live'
import {Banner} from '@primer/react/experimental'
import {useEffect} from 'react'

import {useProjectRouteParams} from '../router/use-project-route-params'
import {PROJECT_SETTINGS_ROUTE} from '../routes'
import {
  getValidationResult,
  MAX_FIELDS_PER_IMPORT,
  MAX_FIELDS_PER_PROJECT,
  ValidationResult,
} from './add-issue-fields-dialog-validation'

type BannerConfig = {
  variant: 'critical' | 'warning'
  title: string
  description: string
  primaryAction?: {
    label: string
    onClick?: () => void
    href?: string
  }
}

function getBannerConfig(
  hasImportError: boolean,
  hasAttemptedSubmit: boolean,
  projectCount: number,
  selectedCount: number,
  onRetry?: () => void,
  projectSettingsPath?: string,
): BannerConfig | null {
  if (hasImportError) {
    return {
      variant: 'critical',
      title: 'Something went wrong adding fields.',
      description: '',
      primaryAction: {
        label: 'Try again',
        onClick: onRetry,
      },
    }
  }

  const validationResult = getValidationResult(selectedCount, projectCount)

  if (validationResult === ValidationResult.FieldLimitReached) {
    return {
      variant: 'warning',
      title: 'Field limit reached',
      description: `You already have ${projectCount} fields in your project. Remove some in order to add more fields.`,
      primaryAction: {
        label: 'Manage fields',
        href: projectSettingsPath,
      },
    }
  }

  if (hasAttemptedSubmit && validationResult === ValidationResult.NoFieldsSelected) {
    return {
      variant: 'warning',
      title: 'No fields selected',
      description: 'Select at least one field to continue.',
    }
  }

  if (validationResult === ValidationResult.WouldExceedProjectLimit) {
    const fieldsToRemove = projectCount + selectedCount - MAX_FIELDS_PER_PROJECT
    return {
      variant: 'warning',
      title: 'Would exceed field limit',
      description: `Adding ${selectedCount} field${
        selectedCount > 1 ? 's' : ''
      } would exceed the ${MAX_FIELDS_PER_PROJECT} field limit. Remove at least ${fieldsToRemove} field${
        fieldsToRemove > 1 ? 's' : ''
      } or select fewer fields to continue.`,
      primaryAction: {
        label: 'Manage fields',
        href: projectSettingsPath,
      },
    }
  }

  if (validationResult === ValidationResult.ExceedsImportLimit) {
    return {
      variant: 'warning',
      title: 'Too many fields selected',
      description: `You can only add ${MAX_FIELDS_PER_IMPORT} at a time. Deselect some fields to continue.`,
    }
  }

  return null
}

type Props = {
  projectCount: number
  selectedCount: number
  hasAttemptedSubmit: boolean
  hasImportError: boolean
  onRetry?: () => void
}

export function AddIssueFieldsDialogBanner({
  projectCount,
  selectedCount,
  hasAttemptedSubmit,
  hasImportError,
  onRetry,
}: Props) {
  const projectRouteParams = useProjectRouteParams()
  const projectSettingsPath = PROJECT_SETTINGS_ROUTE.generatePath(projectRouteParams)

  const bannerConfig = getBannerConfig(
    hasImportError,
    hasAttemptedSubmit,
    projectCount,
    selectedCount,
    onRetry,
    projectSettingsPath,
  )

  const announcement = bannerConfig
    ? bannerConfig.description
      ? `${bannerConfig.title}. ${bannerConfig.description}`
      : bannerConfig.title
    : null

  useEffect(() => {
    if (announcement) {
      announce(announcement)
    }
  }, [announcement])

  if (!bannerConfig) {
    return null
  }

  return (
    <Banner
      variant={bannerConfig.variant}
      title={bannerConfig.title}
      description={bannerConfig.description}
      role={bannerConfig.variant === 'critical' ? 'alert' : undefined}
      className="m-2"
      primaryAction={
        bannerConfig.primaryAction ? (
          <Banner.PrimaryAction
            as={bannerConfig.primaryAction.href ? 'a' : 'button'}
            href={bannerConfig.primaryAction.href}
            onClick={bannerConfig.primaryAction.onClick}
          >
            {bannerConfig.primaryAction.label}
          </Banner.PrimaryAction>
        ) : undefined
      }
    />
  )
}

try{ AddIssueFieldsDialogBanner.displayName ||= 'AddIssueFieldsDialogBanner' } catch {}